import { useMemo, useCallback, useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import FilterPopover from '../../community/residents/components/FilterPopover.jsx';
import UserEmptyState from './UserEmptyState.jsx';

/**
 * Column definitions for the users table.
 * Each column has: key, label, sortable, filterable, required (can't hide).
 */
export const COLUMN_DEFS = [
  { key: 'username', label: 'Username', sortable: true, filterable: true, required: true },
  { key: 'display_name', label: 'Display Name', sortable: true, filterable: true, required: true },
  { key: 'email', label: 'Email', sortable: true, filterable: true, required: false },
  { key: 'phone', label: 'Phone', sortable: true, filterable: false, required: false },
  { key: 'title', label: 'Title', sortable: true, filterable: true, required: false },
  { key: 'user_role_name', label: 'Role', sortable: true, filterable: true, required: true },
  { key: 'is_logged_in', label: 'App Status (Mobile)', sortable: true, filterable: true, required: false },
  { key: 'last_logged_in', label: 'Last Login (Mobile)', sortable: true, filterable: false, required: false },
  { key: 'group_name', label: 'Groups', sortable: false, filterable: true, required: false },
  { key: 'reports', label: 'Reports', sortable: false, filterable: true, required: false },
  { key: 'sip_extension', label: 'SIP Extension', sortable: true, filterable: false, required: false },
];

/**
 * LoginStatusCell — inline status badge with hover popover showing last login time
 * and logged-in device details.
 *
 * Displays a green "Online" or grey "Offline" indicator. On hover, shows a popover
 * with a status line — "Currently logged in as of {time}" when online, or
 * "Not logged in" when offline — and a separate "Last logged in at {time}"
 * subtext line, plus a list of devices the user is currently logged into (if any).
 *
 * `lastLoggedIn` (from `row.last_logged_in`) is shown as a separate line per:
 *   - non-cloud campuses: always (both Online and Offline)
 *   - cloud campuses: only when the user is currently Offline
 * An empty `lastLoggedIn` is rendered as "Never logged in".
 *
 * Exported so the User profile page (BasicInfoTab) can show the same widget.
 */
export const LoginStatusCell = ({ isLoggedIn, lastLoginTime, lastLoggedIn, loggedInDevices, mostRecentLogin, isCloud = false }) => {
  const [open, setOpen] = useState(false);
  const [popoverStyle, setPopoverStyle] = useState({});
  const ref = useRef(null);
  const hoverTimeout = useRef(null);

  const computePosition = () => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const popW = 280;
    const spaceBelow = window.innerHeight - rect.bottom;
    const centerX = rect.left + rect.width / 2;
    const left = Math.max(8, Math.min(centerX - popW / 2, window.innerWidth - popW - 8));
    setPopoverStyle({
      position: 'fixed', zIndex: 9999, left: `${left}px`, width: `${popW}px`,
      top: spaceBelow < 120 ? undefined : `${rect.bottom + 6}px`,
      bottom: spaceBelow < 120 ? `${window.innerHeight - rect.top + 6}px` : undefined,
    });
  };

  const handleMouseEnter = () => {
    clearTimeout(hoverTimeout.current);
    computePosition();
    setOpen(true);
  };

  const handleMouseLeave = () => {
    hoverTimeout.current = setTimeout(() => setOpen(false), 100);
  };

  useEffect(() => {
    if (!open) return;
    const onScroll = () => setOpen(false);
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [open]);

  useEffect(() => () => clearTimeout(hoverTimeout.current), []);

  const loggedIn = !!isLoggedIn;

  // Use mostRecentLogin (from GROUP query) over lastLoginTime for display
  const rawLoginTime = mostRecentLogin || lastLoginTime;
  const loginTime = useMemo(() => {
    if (!rawLoginTime) return '';
    const d = new Date(rawLoginTime);
    if (isNaN(d.getTime())) return rawLoginTime;
    return d.toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  }, [rawLoginTime]);

  // Offline is always "Not logged in"; the last-login detail lives on the
  // subtext line (lastLoggedInDisplay) so the two lines don't duplicate.
  const popoverMessage = loggedIn
    ? (isCloud && loginTime ? `Currently logged in as of ${loginTime}` : 'Currently logged in')
    : 'Not logged in';

  // Last-logged-in display — separate from popoverMessage. Visible on
  // non-cloud campuses regardless of online state, and on cloud campuses
  // only when the user is currently Offline.
  const showLastLoggedIn = !isCloud || !loggedIn;
  const lastLoggedInDisplay = useMemo(() => {
    if (!lastLoggedIn) return 'Never logged in';
    const d = new Date(lastLoggedIn);
    if (isNaN(d.getTime())) return `Last logged in at ${lastLoggedIn}`;
    return `Last logged in at ${d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}`;
  }, [lastLoggedIn]);

  // Parse semicolon-separated device string: "iOS | 17.2 | iPhone 15; Android | 14 | Pixel 8"
  // Only parse for cloud campuses — non-cloud campuses show simple Online/Offline
  const devices = useMemo(() => {
    if (!isCloud || !loggedIn || !loggedInDevices) return [];
    return loggedInDevices
      .split(';')
      .map((d) => d.trim())
      .filter(Boolean)
      .map((entry) => {
        const parts = entry.split('|').map((p) => p.trim());
        return { os: parts[0] || '', version: parts[1] || '', model: parts[2] || '' };
      });
  }, [isCloud, loggedIn, loggedInDevices]);

  return (
    <div
      className="up-login-cell"
      ref={ref}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        type="button"
        className={`up-login-badge ${loggedIn ? 'up-login-badge--online' : 'up-login-badge--offline'}`}
      >
        <span className="up-login-badge__dot" aria-hidden="true" />
        {loggedIn ? 'Online' : 'Offline'}
      </button>
      {open ? (
        <div className="up-login-popover" style={popoverStyle} role="tooltip">
          <span className="up-login-popover__text">
            {popoverMessage}
          </span>
          {showLastLoggedIn ? (
            <span className="up-login-popover__subtext">
              {lastLoggedInDisplay}
            </span>
          ) : null}
          {devices.length > 0 ? (
            <div className="up-login-popover__devices">
              <span className="up-login-popover__devices-label">
                Logged in on {devices.length} device{devices.length !== 1 ? 's' : ''}:
              </span>
              <ul className="up-login-popover__device-list">
                {devices.map((d, i) => (
                  <li key={i} className="up-login-popover__device-item">
                    <svg className="up-login-popover__device-icon" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" />
                    </svg>
                    <span className="up-login-popover__device-info">
                      <span className="up-login-popover__device-model">{d.model || 'Unknown Device'}</span>
                      {d.os || d.version ? (
                        <span className="up-login-popover__device-os">{[d.os, d.version].filter(Boolean).join(' ')}</span>
                      ) : null}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

/**
 * UserGroupsCell — inline pill tags for each group name.
 */
const UserGroupsCell = ({ groupNames }) => {
  if (!groupNames || groupNames.length === 0) {
    return <span className="up-cell-text--muted">&mdash;</span>;
  }

  return (
    <div className="up-group-pills">
      {groupNames.map((name, i) => (
        <span key={`${name}-${i}`} className="up-group-pill">
          {name}
        </span>
      ))}
    </div>
  );
};

/**
 * UserReportsPopoverCell — count badge with hover popover listing report names.
 * Mirrors the UserGroupsPopoverCell pattern.
 */
const UserReportsPopoverCell = ({ reportNames }) => {
  const [open, setOpen] = useState(false);
  const [popoverStyle, setPopoverStyle] = useState({});
  const ref = useRef(null);
  const hoverTimeout = useRef(null);

  if (!reportNames || reportNames.length === 0) {
    return <span className="up-cell-text--muted">&mdash;</span>;
  }

  const computePosition = () => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const popW = 260;
    const spaceBelow = window.innerHeight - rect.bottom;
    const centerX = rect.left + rect.width / 2;
    const left = Math.max(8, Math.min(centerX - popW / 2, window.innerWidth - popW - 8));
    setPopoverStyle({
      position: 'fixed', zIndex: 9999, left: `${left}px`, width: `${popW}px`,
      top: spaceBelow < 120 ? undefined : `${rect.bottom + 6}px`,
      bottom: spaceBelow < 120 ? `${window.innerHeight - rect.top + 6}px` : undefined,
    });
  };

  const handleMouseEnter = () => { clearTimeout(hoverTimeout.current); computePosition(); setOpen(true); };
  const handleMouseLeave = () => { hoverTimeout.current = setTimeout(() => setOpen(false), 100); };

  useEffect(() => {
    if (!open) return;
    const onScroll = () => setOpen(false);
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [open]);

  useEffect(() => () => clearTimeout(hoverTimeout.current), []);

  return (
    <div className="up-login-cell" ref={ref} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <button type="button" className="up-users-badge" onClick={() => { computePosition(); setOpen(!open); }} aria-label={`${reportNames.length} report${reportNames.length !== 1 ? 's' : ''}`}>
        <i className="fe fe-file-text" style={{ fontSize: '12px' }} />
        {reportNames.length}
      </button>
      {open ? (
        <div className="up-users-popover" style={popoverStyle}>
          <div className="up-users-popover__title">Scheduled Reports ({reportNames.length})</div>
          <ul className="up-users-popover__list">
            {reportNames.map((name, i) => (
              <li key={`${name}-${i}`} className="up-users-popover__item">
                <span className="up-users-popover__name">{name}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
};

/**
 * UsersTable — full data table for users with sorting, filtering, sticky header,
 * row selection, row actions, and consistent row heights.
 */
const UsersTable = ({
  data,
  allData,
  visibleColumns,
  sortColumn,
  sortOrder,
  onSort,
  filters,
  onFilterChange,
  selectedIds,
  onSelectRow,
  onSelectAll,
  onEditUser,
  onAddUser,
  onClearFilters,
  myRoleWeight,
  myUsername,
  iAmHigherAuthority,
  roleData,
  onResetPassword,
  onDeleteUser,
  onRegisterSip,
  isCloud = false,
  isGrandstream = false,
  schedulesData = [],
}) => {
  const activeColumns = useMemo(
    () => COLUMN_DEFS.filter((col) => visibleColumns.includes(col.key)),
    [visibleColumns]
  );

  /** Build a map of user_id → array of report description strings */
  const userReportsMap = useMemo(() => {
    const map = {};
    (schedulesData || []).forEach((s) => {
      const ids = (s.report_user_ids || '').split(',').map((id) => id.trim()).filter(Boolean);
      ids.forEach((uid) => {
        if (!map[uid]) map[uid] = [];
        map[uid].push(s.description || '(untitled)');
      });
    });
    return map;
  }, [schedulesData]);

  /** Collect unique filter values from the full (unfiltered) dataset */
  const getFilterValues = useCallback(
    (columnKey) => {
      const values = new Set();
      (allData || data).forEach((row) => {
        if (columnKey === 'group_name') {
          // group_name is a comma-separated string from the API
          const raw = row.group_name;
          if (!raw || raw.trim() === '') {
            values.add('(empty)');
          } else {
            raw.split(',').forEach((g) => {
              const trimmed = g.trim();
              if (trimmed) values.add(trimmed);
            });
          }
        } else if (columnKey === 'reports') {
          const reports = userReportsMap[String(row.user_id)] || [];
          if (reports.length === 0) {
            values.add('(empty)');
          } else {
            reports.forEach((name) => values.add(name));
          }
        } else if (columnKey === 'is_logged_in') {
          values.add(row.is_logged_in ? 'Online' : 'Offline');
        } else {
          const val = row[columnKey];
          values.add(val != null && val !== '' ? String(val) : '(empty)');
        }
      });
      return [...values].sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
    },
    [allData, data, userReportsMap]
  );

  const allSelected = data.length > 0 && data.every((r) => selectedIds.includes(r.user_id));
  const someSelected = selectedIds.length > 0 && !allSelected;

  const renderSortIcon = (columnKey) => {
    if (sortColumn !== columnKey) {
      return (
        <svg className="up-icon-sm up-sort-icon up-sort-icon--neutral" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 15L12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9" />
        </svg>
      );
    }
    return sortOrder === 'asc' ? (
      <svg className="up-icon-sm up-sort-icon up-sort-icon--active" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
      </svg>
    ) : (
      <svg className="up-icon-sm up-sort-icon up-sort-icon--active" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
      </svg>
    );
  };

  /** Renders a single cell value */
  const renderCell = (row, columnKey) => {
    switch (columnKey) {
      case 'username':
        return (
          <Link to={`/profiles/user/${row.user_id}`} className="up-cell-text" style={{ fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-primary)', textDecoration: 'none' }}>
            {row.username}
          </Link>
        );

      case 'display_name':
        return <span className="up-cell-text">{row.display_name}</span>;

      case 'email':
        return row.email ? (
          <span className="up-cell-text">{row.email}</span>
        ) : (
          <span className="up-cell-text--muted">&mdash;</span>
        );

      case 'phone':
        return row.phone ? (
          <span className="up-cell-text up-cell-text--nowrap">{row.phone}</span>
        ) : (
          <span className="up-cell-text--muted">&mdash;</span>
        );

      case 'title':
        return row.title ? (
          <span className="up-cell-text">{row.title}</span>
        ) : (
          <span className="up-cell-text--muted">&mdash;</span>
        );

      case 'user_role_name':
        return row.user_role_name ? (
          <span className="up-role-badge">{row.user_role_name}</span>
        ) : (
          <span className="up-cell-text--muted">&mdash;</span>
        );

      case 'is_logged_in':
        return (
          <LoginStatusCell
            isLoggedIn={row.is_logged_in}
            lastLoginTime={row.last_login_time}
            lastLoggedIn={row.last_logged_in}
            loggedInDevices={row.logged_in_devices}
            mostRecentLogin={row.most_recent_login}
            isCloud={isCloud}
          />
        );

      case 'last_logged_in': {
        // Mirrors the popover's lastLoggedInDisplay formatting — same value
        // (row.last_logged_in), just dropped the "Last logged in at …" prefix
        // since the column header already says it. Null/empty → "Never".
        if (!row.last_logged_in) return <span className="up-cell-text--muted">Never</span>;
        const d = new Date(row.last_logged_in);
        const formatted = isNaN(d.getTime())
          ? row.last_logged_in
          : d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
        return <span className="up-cell-text">{formatted}</span>;
      }

      case 'group_name': {
        const raw = row.group_name;
        const groups = raw ? raw.split(',').map((g) => g.trim()).filter(Boolean) : [];
        return <UserGroupsCell groupNames={groups} />;
      }

      case 'reports': {
        const reports = userReportsMap[String(row.user_id)] || [];
        return <UserReportsPopoverCell reportNames={reports} />;
      }

      case 'sip_extension':
        return row.sip_extension != null && row.sip_extension !== '' ? (
          <span className="up-cell-text">{row.sip_extension}</span>
        ) : (
          <span className="up-cell-text--muted">&mdash;</span>
        );

      default:
        return <span className="up-cell-text">{row[columnKey] || '\u2014'}</span>;
    }
  };

  if (data.length === 0) {
    const hasAnyData = (allData || []).length > 0;
    return (
      <UserEmptyState
        type={hasAnyData ? 'no-results' : 'no-data'}
        onAddUser={hasAnyData ? undefined : onAddUser}
        onClearFilters={hasAnyData ? onClearFilters : undefined}
      />
    );
  }

  return (
    <div className="up-table-wrap">
      <table className="up-table">
        <thead className="up-thead">
          <tr>
            {/* Checkbox column */}
            <th scope="col" className="up-th up-th--checkbox">
              <input
                type="checkbox"
                checked={allSelected}
                ref={(el) => { if (el) el.indeterminate = someSelected; }}
                onChange={onSelectAll}
                className="up-checkbox"
                aria-label="Select all users"
              />
            </th>

            {/* Actions column */}
            <th scope="col" className="up-th up-th--actions">Edit</th>

            {/* Data columns */}
            {activeColumns.map((col) => (
              <th key={col.key} scope="col" className="up-th">
                <div className="up-th__content">
                  {col.sortable ? (
                    <button
                      type="button"
                      onClick={() => onSort(col.key)}
                      className="up-th__sort-btn"
                      aria-label={`Sort by ${col.label}`}
                    >
                      {col.label}
                      {renderSortIcon(col.key)}
                    </button>
                  ) : (
                    <span>{col.label}</span>
                  )}

                  {col.filterable ? (
                    <FilterPopover
                      columnKey={col.key}
                      label={col.label}
                      values={getFilterValues(col.key)}
                      selectedValues={filters[col.key] || []}
                      onChange={onFilterChange}
                    />
                  ) : null}
                </div>
              </th>
            ))}
          </tr>
        </thead>

        <tbody className="up-tbody">
          {data.map((row) => {
            const isSelected = selectedIds.includes(row.user_id);
            const canManageRow = iAmHigherAuthority ? iAmHigherAuthority(row.user_role_weight, myRoleWeight) : true;
            // Edit is permitted for rows the current role outranks OR the user's own row (self-edit).
            const isSelfRow = !!myUsername && row.username === myUsername;
            const canEditRow = canManageRow || isSelfRow;
            return (
              <tr
                key={row.user_id}
                className={`up-tr ${isSelected ? 'is-selected' : ''}`}
              >
                {/* Checkbox */}
                <td className="up-td up-td--checkbox">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => onSelectRow(row.user_id)}
                    className="up-checkbox"
                    disabled={!canManageRow}
                    aria-label={`Select ${row.display_name || row.username}`}
                  />
                </td>

                {/* Row actions kebab */}
                <td className="up-td up-td--actions">
                  <RowActionsMenu
                    user={row}
                    onEdit={onEditUser}
                    onResetPassword={onResetPassword}
                    onDelete={onDeleteUser}
                    onRegisterSip={onRegisterSip}
                    canManage={canManageRow}
                    canEdit={canEditRow}
                    isGrandstream={isGrandstream}
                  />
                </td>

                {/* Data cells */}
                {activeColumns.map((col) => (
                  <td key={col.key} className="up-td">
                    {renderCell(row, col.key)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

/**
 * RowActionsMenu — kebab menu for per-row user actions.
 */
const RowActionsMenu = ({ user, onEdit, onResetPassword, onDelete, onRegisterSip, canManage, canEdit = true, isGrandstream = false }) => {
  // SIP register option only available for grandstream campuses, only when
  // this user doesn't yet have a SIP extension (sip_extension is null or 0).
  const needsSipRegistration = isGrandstream && (user?.sip_extension == null || Number(user?.sip_extension) === 0);
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState({});
  const menuRef = useRef(null);
  // Inline busy state for the "Register SIP Extension" item — keeps the
  // menu open and shows a spinner while the 4-7s round-trip completes,
  // mirroring the drawer save pattern. Closed + toast only fires after.
  const [sipBusy, setSipBusy] = useState(false);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        // Hold the menu open while a SIP registration is in flight so the
        // user sees the spinner; otherwise they'd lose the in-progress
        // indicator from an accidental outside click.
        if (sipBusy) return;
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open, sipBusy]);

  useEffect(() => {
    if (!open) return;
    const onScroll = () => {
      if (sipBusy) return; // see comment above re: holding the menu open
      setOpen(false);
    };
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [open, sipBusy]);

    const handleToggle = () => {
        if (sipBusy) return; // don't allow closing the trigger mid-flight
        if (!open && menuRef.current) {
            const rect = menuRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            if (spaceBelow < 180) {
                setMenuStyle({ position: 'fixed', bottom: `${window.innerHeight - rect.top + 4}px`, right: `${window.innerWidth - rect.right}px`, top: 'auto', left: 'auto', margin: 0, zIndex: 9999 });
            } else {
                setMenuStyle({ position: 'fixed', top: `${rect.bottom + 4}px`, right: `${window.innerWidth - rect.right}px`, bottom: 'auto', left: 'auto', margin: 0, zIndex: 9999 });
            }
        }
        setOpen(!open);
    };

    const handleRegisterSipClick = async () => {
        if (sipBusy) return;
        setSipBusy(true);
        try {
            await onRegisterSip(user);
        } finally {
            setSipBusy(false);
            setOpen(false);
        }
    };

  return (
    <div className="up-row-actions" ref={menuRef}>
      <button
        type="button"
        onClick={handleToggle}
        className="up-row-actions__trigger"
        aria-label={`Actions for ${user.display_name || user.username}`}
        aria-expanded={open}
      >
        <i className="fe fe-more-vertical" aria-hidden="true" />
      </button>

      {open ? (
        <div className="up-row-actions__menu" style={menuStyle}>
          {/* View Profile */}
          <Link
            to={`/profiles/user/${user.user_id}`}
            className="up-row-actions__item"
            style={{ textDecoration: 'none' }}
            onClick={() => setOpen(false)}
          >
            <svg className="up-icon-md up-row-actions__item-icon" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            View Profile
          </Link>

          {/* Edit User — disabled when the current role can't edit this row
              (openEditUser's toast remains as a defense-in-depth check). */}
          <button
            type="button"
            onClick={() => { onEdit(user); setOpen(false); }}
            disabled={!canEdit}
            title={canEdit ? undefined : 'Your role cannot edit this user.'}
            className="up-row-actions__item"
          >
            <svg className="up-icon-md up-row-actions__item-icon" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
            </svg>
            Edit User
          </button>

          {/* Reset Password — only if current user can manage this row */}
          {canManage ? (
            <button
              type="button"
              onClick={() => { onResetPassword(user); setOpen(false); }}
              className="up-row-actions__item"
            >
              <svg className="up-icon-md up-row-actions__item-icon" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" />
              </svg>
              Reset Password
            </button>
          ) : null}

          {/* Register SIP Extension — grandstream campuses only,
              only when the user has no extension assigned yet. The menu
              stays open with an inline spinner during the 4-7s call,
              matching the drawer save pattern. */}
          {needsSipRegistration && onRegisterSip ? (
            <button
              type="button"
              onClick={handleRegisterSipClick}
              disabled={sipBusy}
              aria-busy={sipBusy}
              className="up-row-actions__item"
            >
              {sipBusy ? (
                <svg className="up-icon-md up-row-actions__item-icon up-toast__spinner" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992V4.356M2.985 19.644v-4.992h4.992m-4.992 0a8.25 8.25 0 0014.142 2.515M21.015 9.348a8.25 8.25 0 00-14.142-2.515L2.985 10.86" />
                </svg>
              ) : (
                <svg className="up-icon-md up-row-actions__item-icon" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
                </svg>
              )}
              {sipBusy ? 'Registering…' : 'Register SIP Extension'}
            </button>
          ) : null}

          {/* Delete User — only if current user can manage this row, with danger styling */}
          {canManage ? (
            <button
              type="button"
              onClick={() => { onDelete(user); setOpen(false); }}
              className="up-row-actions__item up-row-actions__item--danger"
            >
              <svg className="up-icon-md up-row-actions__item-icon" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
              </svg>
              Delete User
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default UsersTable;
